/**
 * Lightbox.js
 * Accessible modal image viewer. Portals to document.body, traps focus,
 * locks body scroll, closes on Esc or backdrop click, and returns focus
 * to the element that opened it.
 *
 * Props:
 *   isOpen         boolean
 *   onClose        () => void
 *   src            image source
 *   alt            image alt text (also used as aria-label fallback)
 *   ariaLabel      optional override for dialog aria-label
 *   returnFocusRef optional ref of the element to re-focus on close
 */

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import './Lightbox.css';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Lightbox = ({ isOpen, onClose, src, alt, ariaLabel, returnFocusRef }) => {
  const closeButtonRef = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    // Capture the focus-return target at effect-run time. The ref's .current
    // may point to a different (or unmounted) node by the time cleanup runs,
    // so we snapshot it now. Falls back to document.activeElement at open.
    const nodeToReturnFocusTo = returnFocusRef?.current || document.activeElement;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    if (closeButtonRef.current) closeButtonRef.current.focus();

    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;

      const focusables = dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && (active === first || !dialogRef.current.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKey);

    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = originalOverflow;
      if (nodeToReturnFocusTo && typeof nodeToReturnFocusTo.focus === 'function') {
        nodeToReturnFocusTo.focus();
      }
    };
  }, [isOpen, onClose, returnFocusRef]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div
      className="lightbox-backdrop"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className="lightbox-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || alt}
        ref={dialogRef}
      >
        <button
          type="button"
          className="lightbox-close"
          onClick={onClose}
          aria-label="Close"
          ref={closeButtonRef}
        >
          <X size={22} aria-hidden="true" />
        </button>
        <img src={src} alt={alt} className="lightbox-image" />
      </div>
    </div>,
    document.body
  );
};

export default Lightbox;
